import React from 'react';
import { motion } from 'framer-motion';
import { SearchIcon, SparklesIcon, CalendarDaysIcon, MapPinIcon, UsersIcon, WavesIcon, LifeBuoyIcon, DumbbellIcon, IdCardIcon, CreditCardIcon, CheckIcon, XIcon, TriangleAlertIcon, ArrowRightIcon, ChevronRightIcon, BellIcon, RefreshCwIcon, FileTextIcon, ShieldIcon, SettingsIcon, PlayIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { LoisirsOrchestrator } from './LoisirsOrchestrator';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_SWIMMER = 'https://cdn.magicpatterns.com/patterns/generated-images/b78882f6-12ee-4544-b668-5f9e6d917737.jpg';
const PHOTO_JULIE = 'https://cdn.magicpatterns.com/patterns/generated-images/d90f3c9f-6f3d-4e93-acb2-50157bf54215.jpg';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  }
};
const DETECTED: {
  icon: typeof WavesIcon;
  label: Bilingual;
  hint?: Bilingual;
  status: Bilingual;
  ok: boolean;
}[] = [{
  icon: WavesIcon,
  label: {
    fr: 'Plateau',
    en: 'Facility'
  },
  hint: {
    fr: '(piscine)',
    en: '(pool)'
  },
  status: {
    fr: 'Disponible',
    en: 'Available'
  },
  ok: true
}, {
  icon: UsersIcon,
  label: {
    fr: 'Moniteur',
    en: 'Instructor'
  },
  status: {
    fr: 'Indisponible',
    en: 'Unavailable'
  },
  ok: false
}, {
  icon: LifeBuoyIcon,
  label: {
    fr: 'Sauveteur',
    en: 'Lifeguard'
  },
  status: {
    fr: 'Disponible',
    en: 'Available'
  },
  ok: true
}, {
  icon: UsersIcon,
  label: {
    fr: 'Capacité',
    en: 'Capacity'
  },
  status: {
    fr: '18 / 24 places',
    en: '18 / 24 spots'
  },
  ok: true
}, {
  icon: DumbbellIcon,
  label: {
    fr: 'Équipements',
    en: 'Equipment'
  },
  status: {
    fr: 'Disponible',
    en: 'Available'
  },
  ok: true
}, {
  icon: IdCardIcon,
  label: {
    fr: 'Accès et admissibilité',
    en: 'Access & eligibility'
  },
  status: {
    fr: 'Valides',
    en: 'Valid'
  },
  ok: true
}, {
  icon: CreditCardIcon,
  label: {
    fr: 'Paiements',
    en: 'Payments'
  },
  status: {
    fr: '18 inscriptions payées',
    en: '18 registrations paid'
  },
  ok: true
}];
const ACTIONS: {
  icon: typeof BellIcon;
  label: Bilingual;
}[] = [{
  icon: CalendarDaysIcon,
  label: {
    fr: 'Mettre à jour le cours',
    en: 'Update the class'
  }
}, {
  icon: UsersIcon,
  label: {
    fr: 'Réaffecter la ressource',
    en: 'Reassign the resource'
  }
}, {
  icon: BellIcon,
  label: {
    fr: 'Aviser les 18 inscrits',
    en: 'Notify the 18 registrants'
  }
}, {
  icon: RefreshCwIcon,
  label: {
    fr: 'Mettre à jour l’horaire',
    en: 'Update the schedule'
  }
}, {
  icon: FileTextIcon,
  label: {
    fr: 'Consigner l’action',
    en: 'Log the action'
  }
}];
const FLOW: {
  icon: typeof BellIcon;
  label: Bilingual;
}[] = [{
  icon: SearchIcon,
  label: {
    fr: 'Détecte',
    en: 'Detects'
  }
}, {
  icon: ShieldIcon,
  label: {
    fr: 'Vérifie',
    en: 'Verifies'
  }
}, {
  icon: SparklesIcon,
  label: {
    fr: 'Recommande',
    en: 'Recommends'
  }
}, {
  icon: SettingsIcon,
  label: {
    fr: 'Prépare',
    en: 'Prepares'
  }
}, {
  icon: PlayIcon,
  label: {
    fr: 'Exécute',
    en: 'Executes'
  }
}, {
  icon: UsersIcon,
  label: {
    fr: 'Informe',
    en: 'Informs'
  }
}];
const panel = 'rounded-3xl border border-[#EEF0F4] bg-white p-5 md:p-6 shadow-[0_10px_30px_rgba(18,18,40,0.04)]';
function PanelHeader({
  icon: Icon,
  title,
  desc
}: {
  icon: typeof SearchIcon;
  title: string;
  desc: string;
}) {
  return <div className="flex items-start gap-4" data-id="element-4550" data-mp="1y4qqy943hahh">
      <span className="shrink-0 w-12 h-12 rounded-xl bg-[#F1F1FD] flex items-center justify-center" data-id="element-4551" data-mp="1y5s5inkbkf3y">
        <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-4552" data-mp="235o7kh9ghwgv" />
      </span>
      <div data-id="element-4553" data-mp="284rek78zycrh">
        <h3 className="font-sans text-[18px] font-semibold text-[#121212] leading-tight" data-id="element-4554" data-mp="284hn0f8azxjo">{title}</h3>
        <p className="font-sans text-[13px] text-[#5B6478] mt-1" data-id="element-4555" data-mp="2czwumpfpwx7n">{desc}</p>
      </div>
    </div>;
}
function GreenCheck({
  size = 'sm'
}: {
  size?: 'sm' | 'lg';
}) {
  const box = size === 'lg' ? 'w-8 h-8' : 'w-5 h-5';
  const icon = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';
  return <span className={`shrink-0 ${box} rounded-full bg-[#22A55B] flex items-center justify-center`} data-id="element-4556" data-mp="mreifkxe43oz">
      <CheckIcon className={`${icon} text-white`} strokeWidth={3} aria-hidden="true" data-id="element-4557" data-mp="3nmmbvbzfk6t9" />
    </span>;
}
function Chevron() {
  return <div className="flex items-center justify-center" aria-hidden="true" data-id="element-4558" data-mp="1d30hovxuld3">
      <ChevronRightIcon className="hidden xl:block w-6 h-6 text-[#4F46E5]" strokeWidth={2} data-id="element-4559" data-mp="1ayu90akha9n" />
      <ChevronRightIcon className="xl:hidden w-6 h-6 rotate-90 text-[#4F46E5]" strokeWidth={2} data-id="element-4560" data-mp="3shjkwiwpmg65" />
    </div>;
}
export function LoisirsSolution() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bilingual) => isFR ? v.fr : v.en;
  return <div className="max-w-[1480px] mx-auto" data-id="element-4561" data-mp="3elfpwne0i3sm">
      {/* Intro */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="max-w-5xl" data-id="element-4562" data-mp="39suyfsikw2s9">
        <p className="font-sans text-xs font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-4563" data-mp="2m2sbfqtitn0i">
          {isFR ? 'La solution' : 'The solution'}
        </p>
        <h2 className="font-sans font-semibold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-[-0.03em] text-[#121212] mt-4" data-id="element-4564" data-mp="2h2mpmr9xmuqc">
          {isFR ? 'Loisirs ATNA comprend les dépendances.' : 'ATNA Recreation understands the dependencies.'}
          <br data-id="element-4565" data-mp="nmrscp5h7dwm" />
          <span className="text-[#4F46E5]" data-id="element-4566" data-mp="nnbbuaaryacr">{isFR ? 'Et orchestre toute l’opération.' : 'And orchestrates the entire operation.'}</span>
        </h2>
        <p className="font-sans text-lg text-[#5B6478] leading-relaxed mt-5 max-w-4xl" data-id="element-4567" data-mp="1fd60s5upzy0w">
          {isFR ? 'Une seule plateforme pour gérer activités, participants, plateaux, ressources, équipements, accès, paiements et règles — avec l’intelligence ATNA intégrée pour détecter, vérifier, recommander et agir.' : 'One platform to manage activities, participants, facilities, resources, equipment, access, payments and rules — with built-in ATNA intelligence to detect, verify, recommend and act.'}
        </p>
      </motion.div>

      {/* Detect → Understand → Propose */}
      <div className="mt-12 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto_minmax(540px,1.1fr)_auto_minmax(0,1fr)] gap-5 xl:gap-3 items-center" data-id="element-4568" data-mp="21lbq0ibiye14">
        {/* Detect */}
        <motion.section {...reveal} transition={{
        duration: 0.5,
        ease: EASE
      }} className={`${panel} self-stretch`} data-id="element-4569" data-mp="1wobmae25dnd6">
          <PanelHeader icon={SearchIcon} title={isFR ? 'Ce qu’ATNA détecte' : 'What ATNA detects'} desc={isFR ? 'Vue en temps réel de toutes les dépendances.' : 'Real-time view of every dependency.'} data-id="element-4570" data-mp="1rt5syu6f16f2" />
          <div className="mt-5 rounded-2xl border border-[#F0F1F5] p-3" data-id="element-4571" data-mp="1dupics5fmvrn">
            <div className="flex items-center gap-4 pb-3 border-b border-[#F0F1F5]" data-id="element-4572" data-mp="18w4i4sq6kc37">
              <img src={PHOTO_SWIMMER} alt={isFR ? 'Enfant au cours de natation' : 'Child in a swimming class'} className="w-28 h-20 rounded-xl object-cover shrink-0" data-id="element-4573" data-mp="14v2kuhftqa3v" />
              <div className="min-w-0 font-sans text-[12.5px] text-[#5B6478]" data-id="element-4574" data-mp="2za53pwadatxt">
                <p className="text-[15px] font-semibold text-[#121212]" data-id="element-4575" data-mp="l1uezlgugirs">{isFR ? 'Cours de natation' : 'Swimming class'}</p>
                <p className="flex items-center gap-2 mt-1.5" data-id="element-4576" data-mp="pzdv6qx3r5md">
                  <CalendarDaysIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4577" data-mp="urdz0ugamio8" />
                  {isFR ? 'Mardi 18 h – 19 h' : 'Tuesday 6–7 PM'}
                </p>
                <p className="flex items-center gap-2 mt-1" data-id="element-4578" data-mp="yrxyw5s940e2">
                  <MapPinIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4579" data-mp="23i5tzpxn8w7" />
                  {isFR ? 'Piscine municipale' : 'Municipal pool'}
                </p>
                <p className="flex items-center gap-2 mt-1" data-id="element-4580" data-mp="b34vjo9auq9f">
                  <UsersIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-4581" data-mp="b1lh8377epaa" />
                  {isFR ? '5 à 10 ans · 24 places' : 'Ages 5 to 10 · 24 spots'}
                </p>
              </div>
            </div>
            <ul className="mt-1" data-id="element-4582" data-mp="1mi21c9tpdu3h">
              {DETECTED.map(({
              icon: Icon,
              label,
              hint,
              status,
              ok
            }) => <li key={label.en} className={`grid grid-cols-[20px_1fr_20px_1fr] items-center gap-3 px-2 py-2.5 border-b border-[#F4F5F8] last:border-b-0 ${ok ? '' : 'bg-[#FEF1F0] rounded-lg border-b-transparent'}`} data-id="element-4583" data-mp="1rjhhbnh0xaf0">
                  <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4584" data-mp="176g09t84p8dk" />
                  <span className="font-sans text-[13px] font-semibold text-[#121212] truncate" data-id="element-4585" data-mp="1lv2f7ttufy49">
                    {pick(label)}
                    {hint && <span className="font-normal text-[11px] text-[#5B6478]" data-id="element-4586" data-mp="1gvg0ffyda9tc"> {pick(hint)}</span>}
                  </span>
                  {ok ? <GreenCheck data-id="element-4587" data-mp="1qufbf56luhji" /> : <span className="w-5 h-5 rounded-full bg-[#E5484D] flex items-center justify-center" data-id="element-4588" data-mp="1qux6vzlg0rqd">
                      <XIcon className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" data-id="element-4589" data-mp="i9fft8mwfzb" />
                    </span>}
                  <span className={`font-sans text-[12.5px] truncate ${ok ? 'text-[#5B6478]' : 'text-[#D93036] font-medium'}`} data-id="element-4590" data-mp="3ii3nhnamjxqf">
                    {pick(status)}
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.section>

        <Chevron data-id="element-4591" data-mp="38q7l401i9lge" />

        {/* Understand */}
        <motion.div {...reveal} transition={{
        duration: 0.5,
        delay: 0.08,
        ease: EASE
      }} className="min-w-0 py-4" data-id="element-4592" data-mp="3dq3fr8obc0ja">
          <LoisirsOrchestrator data-id="element-4593" data-mp="2yt2uno0mn3ts" />
        </motion.div>

        <Chevron data-id="element-4594" data-mp="33spcybvbba9g" />

        {/* Propose */}
        <motion.section {...reveal} transition={{
        duration: 0.5,
        delay: 0.16,
        ease: EASE
      }} className={`${panel} self-stretch flex flex-col`} data-id="element-4595" data-mp="33tqqqo3pqauv">
          <PanelHeader icon={SparklesIcon} title={isFR ? 'Ce qu’ATNA propose' : 'What ATNA proposes'} desc={isFR ? 'Des solutions concrètes et prêtes à mettre en œuvre.' : 'Concrete solutions, ready to put in place.'} data-id="element-4596" data-mp="2us9bt921kf7r" />
          <div className="mt-5 flex items-center gap-3 rounded-2xl bg-[#FEF1F0] border border-[#FBE0DD] px-4 py-3.5" data-id="element-4597" data-mp="t04n06fu9y19">
            <TriangleAlertIcon className="w-7 h-7 shrink-0 text-[#E5484D]" strokeWidth={2} aria-hidden="true" data-id="element-4598" data-mp="3pbuzkd0e4r70" />
            <div data-id="element-4599" data-mp="3gczvb6gwpr6c">
              <p className="font-sans text-[15px] font-semibold text-[#D93036]" data-id="element-4600" data-mp="3gdjbahmzy5hq">{isFR ? 'Conflit détecté' : 'Conflict detected'}</p>
              <p className="font-sans text-[12.5px] text-[#5B6478]" data-id="element-4601" data-mp="3kfedrpgnyox3">
                {isFR ? 'Moniteur indisponible mardi à 18 h.' : 'Instructor unavailable Tuesday at 6 PM.'}
              </p>
            </div>
          </div>

          <p className="font-sans text-[15px] font-semibold text-[#121212] mt-5" data-id="element-4602" data-mp="gvs2102zsiwe">
            {isFR ? '2 solutions possibles' : '2 possible solutions'}
          </p>
          <ul className="mt-3 space-y-2.5" aria-hidden="true" data-id="element-4603" data-mp="7vv8ol5nqvv0">
            <li className="flex items-center gap-3 rounded-2xl border-2 border-[#4F46E5]/70 bg-[#F7F7FE] px-3.5 py-3" data-id="element-4604" data-mp="2noltefmp6api">
              <span className="shrink-0 w-5 h-5 rounded-full border-2 border-[#4F46E5] flex items-center justify-center" data-id="element-4605" data-mp="2snoy1yyrmewy">
                <span className="w-2.5 h-2.5 rounded-full bg-[#4F46E5]" data-id="element-4606" data-mp="2k4tqnj5ctf77" />
              </span>
              <span className="flex-1 min-w-0 font-sans text-[12.5px] text-[#5B6478]" data-id="element-4607" data-mp="2g41hsr0e9cbg">
                <span className="block text-[13.5px] font-semibold text-[#121212]" data-id="element-4608" data-mp="2u3jc1lwglhs7">
                  {isFR ? 'Julie est disponible à 18 h 30' : 'Julie is available at 6:30 PM'}
                </span>
                <span className="flex items-center gap-1.5 mt-0.5" data-id="element-4609" data-mp="21gyhkckospkf">
                  {isFR ? 'Qualifications requises' : 'Required qualifications'}
                  <CheckIcon className="w-3.5 h-3.5 text-[#22A55B]" strokeWidth={3} data-id="element-4610" data-mp="1wezihevzceta" />
                </span>
                <span className="flex items-center gap-1.5" data-id="element-4611" data-mp="26ezzuvaagkuu">
                  {isFR ? 'La piscine demeure disponible' : 'The pool remains available'}
                  <CheckIcon className="w-3.5 h-3.5 text-[#22A55B]" strokeWidth={3} data-id="element-4612" data-mp="1inuniq3k4apa" />
                </span>
              </span>
              <img src={PHOTO_JULIE} alt="" className="w-14 h-14 rounded-xl object-cover object-top shrink-0" data-id="element-4613" data-mp="1doi289sc3bot" />
            </li>
            <li className="flex items-center gap-3 rounded-2xl border border-[#EEF0F4] px-3.5 py-3" data-id="element-4614" data-mp="3irezc80dnawp">
              <span className="shrink-0 w-5 h-5 rounded-full border-2 border-[#C9CCD6]" data-id="element-4615" data-mp="3nhvfw0zhvcna" />
              <span className="flex-1 min-w-0 font-sans text-[12.5px] text-[#5B6478]" data-id="element-4616" data-mp="3rjqsi1mvlcgd">
                <span className="block text-[13.5px] font-semibold text-[#121212]" data-id="element-4617" data-mp="2utpudc0m9jme">
                  {isFR ? 'Déplacer le cours au mercredi à 18 h' : 'Move the class to Wednesday at 6 PM'}
                </span>
                <span className="flex items-center gap-1.5 mt-0.5" data-id="element-4618" data-mp="33unz5wivpv61">
                  {isFR ? 'Moniteur disponible' : 'Instructor available'}
                  <CheckIcon className="w-3.5 h-3.5 text-[#22A55B]" strokeWidth={3} data-id="element-4619" data-mp="38v29xmbv7j7r" />
                </span>
                <span className="flex items-center gap-1.5" data-id="element-4620" data-mp="j3nme0bevvu5">
                  {isFR ? 'Piscine disponible' : 'Pool available'}
                  <CheckIcon className="w-3.5 h-3.5 text-[#22A55B]" strokeWidth={3} data-id="element-4621" data-mp="o531zcutla55" />
                </span>
              </span>
            </li>
          </ul>

          <div className="mt-2.5 flex items-start gap-3 rounded-2xl bg-[#EEFAF2] border border-[#D5F0DE] px-3.5 py-3" data-id="element-4622" data-mp="t9f3omuv68lw">
            <GreenCheck size="lg" data-id="element-4623" data-mp="ofastsih9gfj" />
            <div data-id="element-4624" data-mp="kg1x4i3vn8hc">
              <p className="font-sans text-[14px] font-semibold text-[#15803D]" data-id="element-4625" data-mp="kglhs6kwwaz2">
                {isFR ? 'Recommandation : déplacer à 18 h 30' : 'Recommendation: move to 6:30 PM'}
              </p>
              <p className="font-sans text-[12.5px] text-[#5B6478] leading-snug mt-0.5" data-id="element-4626" data-mp="ahcqjlhflage">
                {isFR ? 'Meilleure solution selon les disponibilités, les répercussions et les préférences.' : 'Best option based on availability, impact and preferences.'}
              </p>
            </div>
          </div>

          <span className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#4F46E5] py-3 font-sans text-[14px] font-medium text-white" aria-hidden="true" data-id="element-4627" data-mp="1zug7luvvv5fi">
            {isFR ? 'Préparer le changement' : 'Prepare the change'}
            <ArrowRightIcon className="w-4 h-4" data-id="element-4628" data-mp="27vx9qcgvlwj7" />
          </span>
        </motion.section>
      </div>

      {/* Actions band */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mt-6 rounded-3xl border border-[#EEF0F4] bg-white px-6 py-6 md:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,300px)_1fr] gap-6 items-center" data-id="element-4629" data-mp="1tzjy3jz0wpcn">
        <p className="font-sans text-[17px] font-semibold text-[#4F46E5] leading-snug" data-id="element-4630" data-mp="1ozflw655egs8">
          {isFR ? 'Une fois la solution validée, ATNA prépare et exécute les actions.' : 'Once the solution is approved, ATNA prepares and executes the actions.'}
        </p>
        <ol className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-2" data-id="element-4631" data-mp="1191tgeinifx4">
          {ACTIONS.map(({
          icon: Icon,
          label
        }, i) => <React.Fragment key={label.en} data-id="element-4632" data-mp="1673cizg2uf8l" data-mpi={i}>
              <li className="flex items-center gap-3 min-w-0" data-id="element-4633" data-mp="mlup4rno8g77">
                <span className="shrink-0 w-12 h-12 rounded-full bg-[#F1F1FD] flex items-center justify-center" data-id="element-4634" data-mp="mo7734tfiqk9">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4635" data-mp="hpcq88f0vc2s" />
                </span>
                <span className="font-sans text-[13.5px] text-[#121212] leading-snug max-w-[150px]" data-id="element-4636" data-mp="rlftjhlm1grb">{pick(label)}</span>
              </li>
              {i < ACTIONS.length - 1 && <li aria-hidden="true" className="pl-4 md:pl-0" data-id="element-4637" data-mp="10lvscdrerzpu">
                  <ArrowRightIcon className="w-4 h-4 text-[#4F46E5] rotate-90 md:rotate-0" data-id="element-4638" data-mp="10lci7j802fmd" />
                </li>}
            </React.Fragment>)}
        </ol>
      </motion.div>

      {/* Teams band */}
      <motion.div {...reveal} transition={{
      duration: 0.5,
      ease: EASE
    }} className="mt-5 rounded-3xl bg-[#F7F7FC] px-6 py-6 md:px-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-6 items-center" data-id="element-4639" data-mp="1ae1jjl9u6d3z">
        <div className="lg:pr-8 lg:border-r lg:border-[#E3E4EE]" data-id="element-4640" data-mp="gzqotypbgg4g">
          <p className="font-sans text-[19px] font-semibold text-[#4F46E5]" data-id="element-4641" data-mp="gx4l6a5jqkri">
            {isFR ? 'ATNA ne remplace pas vos équipes.' : 'ATNA doesn’t replace your teams.'}
          </p>
          <p className="font-sans text-[14.5px] text-[#5B6478] mt-1" data-id="element-4642" data-mp="pxj7ncu8y5wl">
            {isFR ? 'Elle leur donne la capacité de voir plus tôt, décider plus vite et agir avec moins de friction.' : 'It gives them the ability to see sooner, decide faster and act with less friction.'}
          </p>
        </div>
        <ol className="grid grid-cols-3 sm:flex sm:items-center gap-y-5 gap-x-3" data-id="element-4643" data-mp="19nwcoubo2l8i">
          {FLOW.map(({
          icon: Icon,
          label
        }, i) => <React.Fragment key={label.en} data-id="element-4644" data-mp="1dnx2fb47uiuv" data-mpi={i}>
              <li className="flex flex-col items-center gap-2" data-id="element-4645" data-mp="3bezmxihn9xsj">
                <span className="w-12 h-12 rounded-full bg-[#ECECFB] flex items-center justify-center" data-id="element-4646" data-mp="3bgkinbfp3mrz">
                  <Icon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-4647" data-mp="3fguzxk8xtzm5" />
                </span>
                <span className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-4648" data-mp="37ywtxppyt6no">{pick(label)}</span>
              </li>
              {i < FLOW.length - 1 && <li aria-hidden="true" className="hidden sm:block pb-6" data-id="element-4649" data-mp="2y7sd0kgqbbq7">
                  <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4650" data-mp="2u6gmmcq16mgy" />
                </li>}
            </React.Fragment>)}
        </ol>
      </motion.div>
    </div>;
}